import { getContextResolver } from "../../../main/core/contextResolver";

/**
 * Bridge minimal shared-chat image helpers to the window object for Excel Lab.
 */
export function bridgeExcelLabWindowBridge(window: Window = globalThis.window): void {
  window.__getContextImageData = async (imageId: string) => {
    try {
      const resolver = getContextResolver();
      const image = resolver.getImage(imageId);
      if (!image?.base64) {
        return null;
      }
      return {
        base64: image.base64,
        mimeType: image.mimeType || "image/png",
      };
    } catch (error) {
      console.warn(`[ExcelLabWindowBridge] Failed to resolve image "${imageId}":`, error);
      return null;
    }
  };

  window.__storeChatInputImage = async (
    base64: string,
    mimeType: string,
    filename?: string
  ): Promise<{ contextId: string; mimeType: string } | null> => {
    try {
      const normalizedMimeType = mimeType.toLowerCase();
      if (normalizedMimeType !== "image/png" && normalizedMimeType !== "image/jpeg") {
        console.warn(
          `[ExcelLabWindowBridge] Unsupported chat image mime type: ${mimeType}. ` +
            "Expected image/png or image/jpeg."
        );
        return null;
      }

      const normalizedBase64 = (base64 || "").trim();
      if (!normalizedBase64 || normalizedBase64.startsWith("data:")) {
        console.warn(
          "[ExcelLabWindowBridge] Invalid chat image payload. Expected raw base64 string."
        );
        return null;
      }

      const resolver = getContextResolver();
      const summary = filename ? `User-attached image: ${filename}` : "User-attached image";
      const contextId = resolver.store("asset", {
        summary,
        filename,
        _imageId: "",
      });

      await resolver.storeImageAsync(contextId, normalizedBase64, normalizedMimeType, {
        source: "chat_input",
        filename,
      });

      resolver.storeWithId(contextId, {
        summary,
        filename,
        _imageId: contextId,
        _imageIds: [contextId],
        mimeType: normalizedMimeType,
      });

      return { contextId, mimeType: normalizedMimeType };
    } catch (error) {
      console.error("[ExcelLabWindowBridge] Failed to store chat input image:", error);
      return null;
    }
  };

  window.__deleteContextImage = async (contextId: string): Promise<boolean> => {
    try {
      const resolver = getContextResolver();
      const imageDeleted = resolver.deleteImage(contextId);
      resolver.delete(contextId);
      return imageDeleted;
    } catch (error) {
      console.warn(`[ExcelLabWindowBridge] Failed to delete image "${contextId}":`, error);
      return false;
    }
  };
}

export function verifyExcelLabWindowBridge(window: Window = globalThis.window): boolean {
  const required = [
    "__getContextImageData",
    "__storeChatInputImage",
    "__deleteContextImage",
  ] as const;

  const missing = required.filter((name) => !window[name as keyof Window]);
  if (missing.length > 0) {
    console.warn("[ExcelLabWindowBridge] Missing bridged functions:", missing);
    return false;
  }
  return true;
}

